import { DOCUMENT } from '@angular/common';
import {
  computed,
  effect,
  inject,
  RendererFactory2,
  untracked,
} from '@angular/core';
import type { Language } from '@indutrust/shared-model';
import { I18N_CONFIG } from '@indutrust/shared-model';
import {
  getLanguageOptions,
  getSerializedNavigatorLanguage,
} from '@indutrust/shared-util';
import { TranslocoService } from '@jsverse/transloco';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';

function getInitialLanguage(): Language {
  const languageCodeSourcesPrioritized = [
    // 1: previous lang set in local storage
    localStorage.getItem('language') ?? '',
    // 2: preferred lang from browser
    getSerializedNavigatorLanguage(),
    // Default
    I18N_CONFIG.defaultLanguage.code,
  ];

  const matchingLanguageCode = languageCodeSourcesPrioritized.find(
    (languageCode) =>
      I18N_CONFIG.languages
        .map((language) => language.code)
        .includes(languageCode)
  );

  return (
    I18N_CONFIG.languages.find(
      (language) => language.code === matchingLanguageCode
    ) ?? I18N_CONFIG.defaultLanguage
  );
}

type I18nState = {
  language: Language;
  isLanguageNotificationVisible: boolean;
};

const initialState: I18nState = {
  language: getInitialLanguage(),
  isLanguageNotificationVisible: false,
};

/**
 * Signal store managing application internationalization (i18n) state.
 *
 * Holds the active {@link Language}, exposes derived values like text direction and
 * language options, and synchronizes Angular, Transloco and PrimeNG locales.
 *
 * @example
 * // Inject and use in a component
 * import { inject } from '@angular/core';
 * import { I18nStore } from '@indutrust/shared-data';
 *
 * export class ExampleComponent {
 *   readonly i18nStore = inject(I18nStore);
 *
 *   changeToEnglish() {
 *     this.i18nStore.updateLanguage(I18N_CONFIG.defaultLanguage);
 *   }
 * }
 */
export const I18nStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ language }) => ({
    direction: computed(() => {
      return language().direction;
    }),
    languageOptions: computed(() => {
      return getLanguageOptions(I18N_CONFIG.languages, language());
    }),
  })),
  withMethods((store) => ({
    /**
     * Updates the current application language.
     *
     * @param language - The new {@link Language} to activate.
     * @example
     * i18nStore.updateLanguage(I18N_CONFIG.languages.find(l => l.code === 'ar')!);
     */
    updateLanguage(language: Language) {
      patchState(store, { language });
    },
    /**
     * Shows or hides the language change notification banner.
     *
     * @param isLanguageNotificationVisible - Whether the banner should be visible.
     * @example
     * i18nStore.updateIsLanguageNotificationVisible(true);
     */
    updateIsLanguageNotificationVisible(
      isLanguageNotificationVisible: boolean
    ) {
      patchState(store, {
        isLanguageNotificationVisible,
      });
    },
  })),
  withHooks((store) => {
    const document = inject(DOCUMENT);
    const transloco = inject(TranslocoService);
    const rendererFactory = inject(RendererFactory2);
    const renderer = rendererFactory.createRenderer(document, null);

    return {
      onInit() {
        effect(() => {
          const language = store.language();

          untracked(() => {
            localStorage.setItem('language', language.code);

            transloco.setActiveLang(language.code);

            document.documentElement.lang = language.code;
            document.documentElement.dir = language.direction;

            renderer.setAttribute(
              document.documentElement,
              'dir',
              language.direction
            );
          });
        });
      },
    };
  })
);
